import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { isNativeApp, isManagementApp, nativeHome } from '../utils/platform';
import { biometricAvailable, verifyBiometric, markUnlocked } from '../utils/biometric';

/* ── Brand panel content ──────────────────────────────────────────────────── */
const FEATURES = [
  {
    title: 'Assessments built for your courses',
    body: 'Timed CAs with instant corrections and performance tracking — exactly like your department exams.',
  },
  {
    title: 'Past questions and AI-guided quiz practice',
    body: 'Access thousands of verified past questions organised by course, topic, and year.',
  },
  {
    title: 'Direct contact with your tutors',
    body: 'Message your tutor, join study groups, and stay on top of every assignment deadline.',
  },
];

const STATS = [
  { value: '1,000+', label: 'Active students' },
  { value: '40+',   label: 'Courses covered' },
  { value: '98%',   label: 'Satisfaction rate' },
];

/* ── Icons ────────────────────────────────────────────────────────────────── */
function IconStack() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2L2 7l10 5 10-5-10-5z" /><path d="M2 17l10 5 10-5" /><path d="M2 12l10 5 10-5" />
    </svg>
  );
}

function IconCheck() {
  return (
    <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
      <circle cx="7.5" cy="7.5" r="6.5" stroke="var(--org)" strokeWidth="1.5" />
      <path d="M4.5 7.5l2 2 4-4" stroke="var(--org)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconEye({ open }) {
  return open ? (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
    </svg>
  ) : (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18">
      <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908C16.658 14.251 17.64 11.943 17.64 9.2z" fill="#4285F4"/>
      <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
      <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
      <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
    </svg>
  );
}

/* ── Input field ──────────────────────────────────────────────────────────── */
function Field({ label, id, type, value, onChange, placeholder, autoComplete, suffix, error }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <label htmlFor={id} style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>{label}</label>
      <div style={{ position: 'relative' }}>
        <input
          id={id}
          name={id}
          type={type || 'text'}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          autoComplete={autoComplete}
          style={{
            width: '100%', boxSizing: 'border-box',
            padding: suffix ? '11px 44px 11px 14px' : '11px 14px',
            fontSize: 14, border: `1.5px solid ${error ? '#f87171' : '#e5e7eb'}`,
            borderRadius: 10, outline: 'none', background: '#fff', color: '#111827',
            transition: 'border-color 0.15s, box-shadow 0.15s',
          }}
          onFocus={e => {
            e.target.style.borderColor = error ? '#f87171' : 'var(--org)';
            e.target.style.boxShadow = error ? '0 0 0 3px rgba(248,113,113,0.1)' : '0 0 0 3px rgba(255,106,0,0.08)';
          }}
          onBlur={e => { e.target.style.borderColor = error ? '#f87171' : '#e5e7eb'; e.target.style.boxShadow = 'none'; }}
        />
        {suffix && (
          <div style={{ position: 'absolute', right: 13, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
            {suffix}
          </div>
        )}
      </div>
      {error && <span style={{ fontSize: 12, color: '#ef4444', marginTop: 2 }}>{error}</span>}
    </div>
  );
}

/* ── Default route helper ─────────────────────────────────────────────────── */
function defaultRoute(user) {
  if (!user) return '/dashboard';
  const r = user.role;
  if (r === 'admin')       return '/admin';
  if (r === 'tutor')       return '/tutor-dashboard';
  if (r === 'jambStudent') return '/jamb/dashboard';
  if (r === 'writer')      return '/news';
  if (r === 'secretary')   return '/secretary';
  if (r === 'loader')      return '/loader-console';
  return '/dashboard';
}

/* Resolve the post-login destination. JAMB accounts are always funneled into
   the JAMB ecosystem and never inherit a `from` path that points into the
   undergrad portal — they don't belong here. */
function resolveDest(user, from) {
  if (user?.role === 'jambStudent') return '/jamb/dashboard';
  // In the native app the home surface is the News Feed, not the dashboard.
  if (isNativeApp()) return from || nativeHome(user);
  return from || defaultRoute(user);
}

// Roles allowed into the "Toppers Management Systems" staff app.
const STAFF_ROLES = ['admin', 'tutor', 'loader', 'writer', 'secretary'];

/* Portal guard — returns an error message when an account may not use this
   entry point, or null when it's allowed through.
   • Staff app (TMS): only staff roles may sign in — students are turned away.
   • University web/app: a JAMB account must use the JAMB portal. */
function portalError(user) {
  if (isManagementApp()) {
    if (user && !STAFF_ROLES.includes(user.role)) {
      return 'This is the Toppers staff app. Students should sign in on the Toppers Tutorial app.';
    }
    return null;
  }
  if (user?.role === 'jambStudent') {
    return 'This is a JAMB account. Please sign in through the JAMB portal at /jamb/signin.';
  }
  return null;
}

/* ══════════════════════════════════════════════════════════════════════════ */
export default function SignIn() {
  const { login, login2FA, loginWithGoogle, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from     = location.state?.from?.pathname;

  const [identifier,  setIdentifier]  = useState('');
  const [password,    setPassword]    = useState('');
  const [showPass,    setShowPass]    = useState(false);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState('');
  const [fieldErr,    setFieldErr]    = useState({});

  const [step,       setStep]       = useState('login'); // 'login' | '2fa'
  const [pending2FA, setPending2FA] = useState(null);
  const [totp,       setTotp]       = useState('');
  const [totpErr,    setTotpErr]    = useState('');
  const [totpBusy,   setTotpBusy]  = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);
  const [bioReady, setBioReady] = useState(false);

  // Offer biometric sign-in in the app when a session was remembered on a
  // previous login and the device has a fingerprint/face sensor.
  useEffect(() => {
    if (isNativeApp() && localStorage.getItem('tt_bio_token')) {
      biometricAvailable().then(r => setBioReady(!!r.available)).catch(() => {});
    }
  }, []);

  const rememberBiometric = () => {
    if (!isNativeApp()) return;
    const t = localStorage.getItem('tt_token');
    const u = localStorage.getItem('tt_user');
    if (t) localStorage.setItem('tt_bio_token', t);
    if (u) localStorage.setItem('tt_bio_user', u);
  };

  const bioLogin = async () => {
    const ok = await verifyBiometric('Sign in to Toppers Tutorial');
    if (!ok) return;
    const t = localStorage.getItem('tt_bio_token');
    const u = localStorage.getItem('tt_bio_user');
    if (!t) return;
    localStorage.setItem('tt_token', t);
    if (u) localStorage.setItem('tt_user', u);
    markUnlocked(); // so BiometricGate doesn't prompt again right after the reload
    let dest = '/feed';
    try { dest = nativeHome(JSON.parse(u)); } catch {}
    window.location.href = dest; // full reload so AuthContext picks up the session
  };

  // Native Google sign-in for the app (the GSI web button can't render inside a
  // WebView). Uses the device account picker via the Capacitor plugin.
  const nativeGoogle = async () => {
    setGoogleBusy(true); setError('');
    try {
      const { GoogleAuth } = await import('@codetrix-studio/capacitor-google-auth');
      try { await GoogleAuth.initialize({ clientId: process.env.REACT_APP_GOOGLE_CLIENT_ID, scopes: ['profile', 'email'] }); } catch {}
      const res = await GoogleAuth.signIn();
      const idToken = res?.authentication?.idToken;
      if (!idToken) throw new Error('no-token');
      const user = await loginWithGoogle(idToken);
      const pErr = portalError(user);
      if (pErr) { logout(); setError(pErr); return; }
      rememberBiometric();
      navigate(resolveDest(user, from), { replace: true });
    } catch (e) {
      const msg = e?.message || '';
      // Silent on user cancel; otherwise show a friendly error.
      if (!/canceled|cancelled|12501|popup_closed/i.test(msg)) {
        setError('Google sign-in failed. Please try again.');
      }
    } finally { setGoogleBusy(false); }
  };

  const googleCbRef  = useRef(null);
  const googleBtnRef = useRef(null);

  /* ── Google sign-in — stable ref-callback pattern ─── */
  const handleGoogleCb = useCallback(async ({ credential }) => {
    setGoogleBusy(true); setError('');
    try {
      const user = await loginWithGoogle(credential);
      const pErr = portalError(user);
      if (pErr) { logout(); setError(pErr); return; }
      rememberBiometric();
      navigate(resolveDest(user, from), { replace: true });
    } catch (e) {
      setError(e.response?.data?.message || 'Google sign-in failed. Please try again.');
    } finally { setGoogleBusy(false); }
  }, [loginWithGoogle, navigate, from]); // eslint-disable-line

  // Keep latest callback in a ref so the renderButton closure never goes stale
  useEffect(() => { googleCbRef.current = handleGoogleCb; }, [handleGoogleCb]);

  // Stable node-callback: called whenever the div mounts/unmounts
  const googleNodeCb = useCallback((node) => {
    googleBtnRef.current = node;
    if (!node) return;
    const render = () => {
      if (!window.google?.accounts?.id || !process.env.REACT_APP_GOOGLE_CLIENT_ID) return;
      window.google.accounts.id.initialize({
        client_id:             process.env.REACT_APP_GOOGLE_CLIENT_ID,
        callback:              (r) => googleCbRef.current?.(r),
        auto_select:           false,
        cancel_on_tap_outside: false,
      });
      window.google.accounts.id.renderButton(node, {
        type:           'standard',
        size:           'large',
        width:          node.clientWidth || 400,
        text:           'continue_with',
        theme:          'outline',
        logo_alignment: 'center',
        shape:          'rectangular',
      });
    };
    if (window.google?.accounts?.id) { render(); return; }
    const script = document.querySelector('script[src*="accounts.google.com/gsi"]');
    if (script) script.addEventListener('load', render, { once: true });
  }, []); // eslint-disable-line

  /* ── Form submit ─── */
  const validate = () => {
    const e = {};
    if (!identifier.trim()) e.identifier = 'This field is required.';
    if (!password)          e.password   = 'This field is required.';
    setFieldErr(e);
    return !Object.keys(e).length;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true); setError('');
    try {
      const result = await login(identifier.trim(), password);
      if (result?.requires2FA) {
        setPending2FA({ userId: result.userId });
        setStep('2fa');
        return;
      }
      const pErr = portalError(result);
      if (pErr) { logout(); setError(pErr); return; }
      rememberBiometric();
      navigate(resolveDest(result, from), { replace: true });
    } catch (e) {
      setError(e.response?.data?.message || 'Sign in failed. Please check your details and try again.');
    } finally { setLoading(false); }
  };

  const handle2FA = async e => {
    e.preventDefault();
    const code = totp.replace(/\D/g, '');
    if (code.length < 6) { setTotpErr('Please enter the full 6-digit code.'); return; }
    setTotpBusy(true); setTotpErr('');
    try {
      const user = await login2FA(pending2FA.userId, code);
      const pErr = portalError(user);
      if (pErr) { logout(); setStep('login'); setError(pErr); return; }
      rememberBiometric();
      navigate(resolveDest(user, from), { replace: true });
    } catch (e) {
      setTotpErr(e.response?.data?.message || 'Invalid code. Please try again.');
    } finally { setTotpBusy(false); }
  };

  /* ── Render ─── */
  return (
    <div style={{ minHeight: '100vh', display: 'flex', fontFamily: 'var(--font, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif)' }}>

      {/* ══ Left brand panel ══ */}
      <div className="auth-panel" style={{
        flex: '0 0 42%', background: '#0c1220', display: 'flex', flexDirection: 'column',
        padding: '44px 48px', position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: -100, right: -100, width: 360, height: 360, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,106,0,0.13) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: -60, left: -60, width: 240, height: 240, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,106,0,0.06) 0%, transparent 70%)', pointerEvents: 'none' }} />

        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 60, position: 'relative', zIndex: 1 }}>
          <div style={{ width: 38, height: 38, borderRadius: 10, background: 'var(--org)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', flexShrink: 0 }}>
            <IconStack />
          </div>
          <span style={{ fontSize: 17, fontWeight: 800, color: '#fff', letterSpacing: '-0.02em' }}>Toppers Tutorial</span>
        </div>

        {/* Copy */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', position: 'relative', zIndex: 1 }}>
          <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: 'var(--org)', margin: '0 0 14px' }}>University of Ibadan</p>
          <h1 style={{ fontSize: 'clamp(24px, 2.8vw, 32px)', fontWeight: 800, color: '#fff', lineHeight: 1.22, margin: '0 0 18px', letterSpacing: '-0.025em' }}>
            Built for students<br />who refuse to settle.
          </h1>
          <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.48)', lineHeight: 1.72, margin: '0 0 44px', maxWidth: 330 }}>
            Academic tools crafted for UI science undergraduates in 100L, 200L, and 300L.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
            {FEATURES.map((f, i) => (
              <div key={i} style={{ display: 'flex', gap: 13, alignItems: 'flex-start' }}>
                <div style={{ marginTop: 2, flexShrink: 0 }}><IconCheck /></div>
                <div>
                  <p style={{ margin: 0, fontSize: 13.5, fontWeight: 700, color: '#fff', lineHeight: 1.3 }}>{f.title}</p>
                  <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'rgba(255,255,255,0.42)', lineHeight: 1.55 }}>{f.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Stats */}
        <div style={{ display: 'flex', gap: 28, paddingTop: 32, borderTop: '1px solid rgba(255,255,255,0.07)', position: 'relative', zIndex: 1, marginTop: 44 }}>
          {STATS.map((s, i) => (
            <div key={i}>
              <p style={{ margin: 0, fontSize: 20, fontWeight: 800, color: 'var(--org)', lineHeight: 1 }}>{s.value}</p>
              <p style={{ margin: '4px 0 0', fontSize: 11, color: 'rgba(255,255,255,0.38)', fontWeight: 500 }}>{s.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ══ Right form panel ══ */}
      <div style={{ flex: 1, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 28px', minHeight: '100vh' }}>
        <div style={{ width: '100%', maxWidth: 400 }}>

          {step === 'login' && (
            <>
              <div style={{ marginBottom: 34 }}>
                <h2 style={{ margin: '0 0 8px', fontSize: 26, fontWeight: 800, color: '#111827', letterSpacing: '-0.025em' }}>Welcome back</h2>
                <p style={{ margin: 0, fontSize: 14, color: '#6b7280' }}>Sign in to continue to your Toppers account.</p>
              </div>

              {/* Biometric sign-in (app only, when a session was remembered) */}
              {bioReady && (
                <button type="button" onClick={bioLogin}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '12px', marginBottom: 18, border: '1.5px solid var(--org)', borderRadius: 12, background: 'rgba(255,106,0,0.06)', color: 'var(--org)', fontSize: 14.5, fontWeight: 700, cursor: 'pointer' }}>
                  <span className="bio-pulse" style={{ display: 'flex' }}>
                    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 11c0 2.5-.4 5-1 6.5" />
                      <path d="M15.5 6.5A6 6 0 0 0 6 11c0 2 0 3.5-.5 5" />
                      <path d="M9 11a3 3 0 0 1 6 0c0 2-.3 4.5-1 6" />
                      <path d="M18 12c0-3.3-2.7-6-6-6a6 6 0 0 0-2 .35" />
                      <path d="M4.5 8.5A8 8 0 0 1 20 11" />
                      <path d="M12 14v.01" />
                    </svg>
                  </span>
                  Sign in with biometrics
                </button>
              )}

              {/* Google sign-in button — rendered by GSI for reliable cross-device support */}
              <div style={{ marginBottom: 22, minHeight: 44 }}>
                {googleBusy ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '11px', border: '1.5px solid #e5e7eb', borderRadius: 10, color: '#6b7280', fontSize: 14 }}>
                    <SmSpinner /> Signing you in…
                  </div>
                ) : isNativeApp() ? (
                  <button type="button" onClick={nativeGoogle}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '11px', border: '1.5px solid #e5e7eb', borderRadius: 10, background: '#fff', color: '#3c4043', fontSize: 14.5, fontWeight: 600, cursor: 'pointer' }}>
                    <GoogleIcon /> Continue with Google
                  </button>
                ) : (
                  <div ref={googleNodeCb} style={{ width: '100%', display: 'flex', justifyContent: 'center' }} />
                )}
              </div>

              {/* Divider */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 22 }}>
                <div style={{ flex: 1, height: 1, background: '#f0f0f0' }} />
                <span style={{ fontSize: 12, color: '#9ca3af', fontWeight: 500, whiteSpace: 'nowrap' }}>or sign in with email</span>
                <div style={{ flex: 1, height: 1, background: '#f0f0f0' }} />
              </div>

              {/* Error */}
              {error && (
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 9, padding: '10px 14px', marginBottom: 18, fontSize: 13.5, color: '#b91c1c', lineHeight: 1.5 }}>
                  {error}
                </div>
              )}

              {/* Email / password form */}
              <form onSubmit={handleSubmit} noValidate style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                <Field
                  label="Email, phone or Tutorial ID"
                  id="identifier"
                  value={identifier}
                  onChange={e => { setIdentifier(e.target.value); setFieldErr(f => ({ ...f, identifier: '' })); setError(''); }}
                  placeholder="you@example.com"
                  autoComplete="username"
                  error={fieldErr.identifier}
                />

                <Field
                  label="Password"
                  id="password"
                  type={showPass ? 'text' : 'password'}
                  value={password}
                  onChange={e => { setPassword(e.target.value); setFieldErr(f => ({ ...f, password: '' })); setError(''); }}
                  placeholder="Your password"
                  autoComplete="current-password"
                  error={fieldErr.password}
                  suffix={
                    <span onClick={() => setShowPass(v => !v)} role="button" aria-label={showPass ? 'Hide password' : 'Show password'} style={{ cursor: 'pointer', display: 'flex', color: '#9ca3af' }}>
                      <IconEye open={showPass} />
                    </span>
                  }
                />

                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: -6 }}>
                  <Link to="/forgot-password" style={{ fontSize: 13, color: 'var(--org)', fontWeight: 600, textDecoration: 'none' }}>
                    Forgot password?
                  </Link>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    width: '100%', padding: '13px', borderRadius: 10, border: 'none',
                    background: 'var(--org)', color: '#fff', fontSize: 15, fontWeight: 700,
                    cursor: loading ? 'default' : 'pointer', opacity: loading ? 0.85 : 1,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    marginTop: 2,
                  }}
                  onMouseEnter={e => { if (!loading) e.currentTarget.style.background = 'var(--org-dk)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'var(--org)'; }}
                >
                  {loading ? <><Spinner />&nbsp;Signing in…</> : 'Sign in'}
                </button>
              </form>

              <p style={{ margin: '28px 0 0', fontSize: 14, color: '#6b7280', textAlign: 'center' }}>
                New to Toppers?{' '}
                <Link to="/signup" style={{ color: 'var(--org)', fontWeight: 700, textDecoration: 'none' }}>
                  Create an account
                </Link>
              </p>

              {/* PC/tablet pairing entry point — without this the desktop app
                  (no address bar) has no way to reach /pair. Hidden on the
                  phone app, where pairing is started from Profile instead. */}
              {!isNativeApp() && (
                <p style={{ margin: '10px 0 0', fontSize: 13, color: '#6b7280', textAlign: 'center' }}>
                  Already signed in on your phone?{' '}
                  <Link to="/pair" style={{ color: 'var(--org)', fontWeight: 700, textDecoration: 'none' }}>
                    Sync this device
                  </Link>
                </p>
              )}
            </>
          )}

          {step === '2fa' && (
            <TwoFAStep
              totp={totp}
              setTotp={setTotp}
              error={totpErr}
              loading={totpBusy}
              onSubmit={handle2FA}
              onBack={() => { setStep('login'); setTotp(''); setTotpErr(''); }}
            />
          )}
        </div>
      </div>

      <style>{`
        @keyframes auth-spin { to { transform: rotate(360deg); } }
        @keyframes bioPulse { 0%,100% { opacity: 0.55; transform: scale(0.94); } 50% { opacity: 1; transform: scale(1.08); } }
        .bio-pulse { animation: bioPulse 1.6s ease-in-out infinite; }
        @media (max-width: 740px) { .auth-panel { display: none !important; } }
      `}</style>
    </div>
  );
}

/* ── 2FA step ─────────────────────────────────────────────────────────────── */
function TwoFAStep({ totp, setTotp, error, loading, onSubmit, onBack }) {
  return (
    <div>
      <div style={{ marginBottom: 32 }}>
        <div style={{ width: 54, height: 54, borderRadius: 14, background: 'rgba(255,106,0,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20 }}>
          <IconShield />
        </div>
        <h2 style={{ margin: '0 0 8px', fontSize: 24, fontWeight: 800, color: '#111827', letterSpacing: '-0.02em' }}>Two-step verification</h2>
        <p style={{ margin: 0, fontSize: 14, color: '#6b7280', lineHeight: 1.65 }}>
          Open your authenticator app and enter the 6-digit code for your Toppers account.
        </p>
      </div>

      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 9, padding: '10px 14px', marginBottom: 18, fontSize: 13.5, color: '#b91c1c' }}>
          {error}
        </div>
      )}

      <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>Authentication code</label>
          <input
            autoFocus
            type="text"
            inputMode="numeric"
            maxLength={6}
            value={totp}
            onChange={e => setTotp(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="000000"
            style={{
              padding: '14px 16px', fontSize: 24, fontWeight: 700, letterSpacing: '0.22em',
              textAlign: 'center', border: '1.5px solid #e5e7eb', borderRadius: 12,
              outline: 'none', color: '#111827', width: '100%', boxSizing: 'border-box',
              fontFamily: 'monospace',
            }}
            onFocus={e => { e.target.style.borderColor = 'var(--org)'; e.target.style.boxShadow = '0 0 0 3px rgba(255,106,0,0.09)'; }}
            onBlur={e => { e.target.style.borderColor = '#e5e7eb'; e.target.style.boxShadow = 'none'; }}
          />
        </div>

        <button
          type="submit"
          disabled={loading || totp.length < 6}
          style={{
            width: '100%', padding: '13px', borderRadius: 10, border: 'none',
            background: loading || totp.length < 6 ? '#fdba74' : 'var(--org)', color: '#fff',
            fontSize: 15, fontWeight: 700, cursor: loading || totp.length < 6 ? 'default' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          }}
        >
          {loading ? <><Spinner />&nbsp;Verifying…</> : 'Verify and sign in'}
        </button>
      </form>

      <button
        type="button"
        onClick={onBack}
        style={{ marginTop: 20, background: 'none', border: 'none', cursor: 'pointer', fontSize: 13.5, color: '#6b7280', fontWeight: 600, padding: 0, display: 'flex', alignItems: 'center', gap: 5 }}
      >
        ← Back to sign in
      </button>

      <p style={{ marginTop: 24, fontSize: 12.5, color: '#9ca3af', lineHeight: 1.6 }}>
        Lost access to your authenticator? Enter one of your backup codes, or contact the administrator.
      </p>
    </div>
  );
}

/* ── Spinners ─────────────────────────────────────────────────────────────── */
function Spinner() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" style={{ animation: 'auth-spin 0.7s linear infinite', flexShrink: 0 }}>
      <circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.3)" strokeWidth="3" />
      <path d="M12 2a10 10 0 0 1 10 10" stroke="#fff" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

function SmSpinner() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ animation: 'auth-spin 0.7s linear infinite' }}>
      <circle cx="12" cy="12" r="10" stroke="#e5e7eb" strokeWidth="3" />
      <path d="M12 2a10 10 0 0 1 10 10" stroke="var(--org)" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

function IconShield() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="var(--org)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    </svg>
  );
}
